import { useMemo } from 'react'

import { BookingBtn, IconText } from '@hhgtech/hhg-components'
import { domainLocales } from 'domainLocales'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { Author, ArticleExpert, ReviewedApi } from 'interfaces/types'
import { useIntl } from 'react-intl'

import { DEFAULT_AVATAR_IMG } from '../../../interfaces/constants'
import { TableOfContent } from '../tableOfContent'
import {
  StyledAuthorAndDate,
  StyledAvatarReviewer,
  StyledDotSeparator,
} from './index.styled'

export type Props = {
  size?: 'lg' | 'md' | 'sm'
  className?: string
  author: Author
  reviewer?: Author
  articleReviewed?: ReviewedApi
  updatedAt: string
  expert?: ArticleExpert
  typeUpdate?: string
}

const AuthorAndDate = ({
  className,
  size = 'md',
  reviewer,
  articleReviewed,
  author,
  expert,
  updatedAt,
  typeUpdate,
}: Props) => {
  const { formatMessage: f } = useIntl()
  const router = useRouterExtend()
  const { locale } = router

  const sL = locale || 'vi-VN' // standardized locale

  const { reviewContent, avatar, badge, avatarLink } = useMemo(() => {
    if (typeof articleReviewed !== 'undefined') {
      const { fact_check, editor, is_author } = articleReviewed
      if (articleReviewed?.expert) {
        if (is_author) {
          return {
            reviewContent: [
              <span
                key={2}
                className="reviewer-info"
                data-is-unclickable={articleReviewed?.expert?.turn_off_profile}
                dangerouslySetInnerHTML={{
                  __html: f(
                    {
                      id:
                        typeUpdate === 'published'
                          ? 'articlePage.reviewed.published'
                          : 'articlePage.reviewed.lastUpdated',
                      defaultMessage:
                        typeUpdate === 'published'
                          ? 'Written by {name} Published {date}'
                          : 'Written by {name} Last Updated {date}',
                    },
                    {
                      name: `<a data-event-category="Author Name" data-event-action="${
                        articleReviewed?.expert?.display_name
                      }" data-event-label="https://${domainLocales[sL]}${
                        articleReviewed?.expert?.profile_url
                      }" ${
                        articleReviewed?.expert?.profile_url
                          ? `href="${articleReviewed?.expert?.profile_url}"`
                          : ''
                      } >${articleReviewed?.expert?.display_name}</a>`,
                      date: updatedAt,
                    },
                  ),
                }}
              />,
            ],
            avatar: articleReviewed?.expert?.avatar_thumbnail || '',
            avatarLink: articleReviewed?.expert?.profile_url,
            badge: articleReviewed?.expert?.role || '',
          }
        } else {
          return {
            reviewContent: [
              <span
                key={1}
                className="reviewer-info"
                data-is-unclickable={articleReviewed?.author?.turn_off_profile}
                dangerouslySetInnerHTML={{
                  __html: f(
                    {
                      id:
                        typeUpdate === 'published'
                          ? 'articlePage.reviewed.published'
                          : 'articlePage.reviewed.lastUpdated',
                      defaultMessage:
                        typeUpdate === 'published'
                          ? 'Written by {name}'
                          : 'Written by {name}',
                    },
                    {
                      name: `<a data-event-category="Author Name" data-event-action="${
                        articleReviewed?.author?.display_name || ''
                      }" data-event-label="https://${domainLocales[sL]}${
                        articleReviewed?.author?.profile_url
                      }" ${
                        articleReviewed?.author?.profile_url
                          ? `href="${articleReviewed?.author?.profile_url}"`
                          : ''
                      }>${articleReviewed?.author?.display_name}</a>`,
                    },
                  ),
                }}
              />,
              <span
                key={1}
                className="reviewer-info"
                data-is-unclickable={articleReviewed?.expert?.turn_off_profile}
                dangerouslySetInnerHTML={{
                  __html: f(
                    {
                      id:
                        articleReviewed?.expert?.role === 'medical'
                          ? 'articlePage.reviewed.medically'
                          : 'articlePage.reviewed.expertly',
                      defaultMessage:
                        articleReviewed?.expert?.role === 'medical'
                          ? 'Medically reviewed by {name}'
                          : 'Expertly reviewed by {name}',
                    },
                    {
                      name: `<a data-event-category="Reviewer Name" data-event-action="${
                        articleReviewed?.expert?.display_name || ''
                      }" data-event-label="https://${domainLocales[sL]}${
                        articleReviewed?.expert?.profile_url
                      }" ${
                        articleReviewed?.expert?.profile_url
                          ? `href="${articleReviewed?.expert?.profile_url}"`
                          : ''
                      } >${articleReviewed?.expert?.display_name}</a>`,
                    },
                  ),
                }}
              />,
              <span
                key={3}
                className="reviewer-info"
                dangerouslySetInnerHTML={{
                  __html: f(
                    { id: 'articlePage.articleUpdatedAt' },
                    {
                      date: updatedAt,
                    },
                  ),
                }}
              />,
            ],
            avatar: articleReviewed?.expert?.avatar_thumbnail || '',
            avatarLink: articleReviewed?.expert?.profile_url,
            badge: articleReviewed?.expert?.role || '',
          }
        }
      } else {
        const factCheckReviewer =
          editor && editor?.user_id !== articleReviewed?.author?.user_id
            ? editor
            : fact_check
        return {
          reviewContent: [
            <span
              key={1}
              className="reviewer-info"
              data-is-unclickable={articleReviewed?.author?.turn_off_profile}
              dangerouslySetInnerHTML={{
                __html: f(
                  {
                    id:
                      typeUpdate === 'published'
                        ? 'articlePage.reviewed.published'
                        : 'articlePage.reviewed.lastUpdated',
                    defaultMessage:
                      typeUpdate === 'published'
                        ? 'Written by {name}'
                        : 'Written by {name}',
                  },
                  {
                    name: `<a data-event-category="Author Name" data-event-action="${
                      articleReviewed?.author?.display_name || ''
                    }" data-event-label="https://${domainLocales[sL]}${
                      articleReviewed?.author?.profile_url
                    }" ${
                      articleReviewed?.author?.profile_url
                        ? `href="${articleReviewed?.author?.profile_url}"`
                        : ''
                    } >${articleReviewed?.author?.display_name}</a>`,
                    // date: updatedAt,
                  },
                ),
              }}
            />,
            <span
              key={1}
              className="reviewer-info"
              data-is-unclickable={factCheckReviewer?.turn_off_profile}
              dangerouslySetInnerHTML={{
                __html: f(
                  {
                    id: 'articlePage.reviewed.factCheck',
                  },
                  {
                    name: `<a data-event-category="Reviewer Name" data-event-action="${
                      factCheckReviewer?.display_name || ''
                    }" data-event-label="https://${domainLocales[sL]}${
                      factCheckReviewer?.profile_url || ''
                    }" ${
                      factCheckReviewer?.profile_url
                        ? `href="${factCheckReviewer?.profile_url || '#'}"`
                        : ''
                    } >${factCheckReviewer?.display_name || ''}</a>`,
                  },
                ),
              }}
            />,
            <span
              key={3}
              className="reviewer-info"
              dangerouslySetInnerHTML={{
                __html: f(
                  { id: 'articlePage.articleUpdatedAt' },
                  {
                    date: updatedAt,
                  },
                ),
              }}
            />,
          ],
          avatar: factCheckReviewer?.avatar_thumbnail || '',
          avatarLink: factCheckReviewer?.profile_url || '',
          badge: 'fact-check',
        }
      }
    }
    return { reviewContent: null, avatar: null, avatarLink: null, badge: null }
  }, [])

  return (
    <StyledAuthorAndDate data-size={size} className={className}>
      <IconText
        size={size}
        iconPosition="left"
        icon={
          <StyledAvatarReviewer>
            <a
              href={
                avatarLink || expert?.permalink || reviewer?.profileUrl || '#'
              }
              data-event-category="Reviewer Name"
              // data-event-action={`${expert?.name || reviewer?.displayName}`}
              // data-event-label={`https://${domainLocales[sL]}${
              //   expert?.permalink || reviewer?.profileUrl
              // }`}
            >
              <img
                loading="lazy"
                src={
                  avatar ||
                  expert?.thumbnailAvatar ||
                  reviewer?.thumbnailAvatar ||
                  author?.thumbnailAvatar ||
                  DEFAULT_AVATAR_IMG
                }
                className="avatar"
                alt={f({ id: 'articlePage.authorAvatarAlt' })}
              />
              {badge && (
                <img
                  loading="lazy"
                  src={`/images/reviewer-${badge}.svg`}
                  alt="badge"
                  className="badge"
                />
              )}
            </a>
          </StyledAvatarReviewer>
        }
      >
        <TableOfContent
          contents={
            reviewContent
              ? reviewContent
              : [
                  typeUpdate === 'published' ? (
                    <span
                      key={2}
                      className="reviewer-info"
                      dangerouslySetInnerHTML={{
                        __html: f(
                          {
                            id: 'articlePage.reviewed.published',
                            defaultMessage:
                              'Written by {name} Published {date}',
                          },
                          {
                            name: expert?.name
                              ? `<a data-event-category="Author Name" data-event-action="${expert?.name}" data-event-label="https://${domainLocales[sL]}${expert?.permalink}" href="${expert?.permalink}">${expert?.name}</a>`
                              : `<a data-event-category="Author Name" data-event-action="${author?.displayName}" data-event-label="https://${domainLocales[sL]}${author?.profileUrl}" href="${author?.profileUrl}">${author?.displayName}</a>`,
                            date: updatedAt,
                          },
                        ),
                      }}
                    />
                  ) : (
                    <span
                      key={2}
                      className="reviewer-info"
                      dangerouslySetInnerHTML={{
                        __html: f(
                          {
                            id: 'articlePage.reviewed.lastUpdated',
                            defaultMessage: 'Written by {name}',
                          },
                          {
                            name: expert?.name
                              ? `<a data-event-category="Author Name" data-event-action="${expert?.name}" data-event-label="https://${domainLocales[sL]}${expert?.permalink}" href="${expert?.permalink}">${expert?.name}</a>`
                              : `<a data-event-category="Author Name" data-event-action="${author?.displayName}" data-event-label="https://${domainLocales[sL]}${author?.profileUrl}" href="${author?.profileUrl}">${author?.displayName}</a>`,
                            date: updatedAt,
                          },
                        ),
                      }}
                    />
                  ),
                  expert?.name || reviewer?.displayName ? (
                    <span
                      key={1}
                      className="reviewer-info"
                      dangerouslySetInnerHTML={{
                        __html: f(
                          { id: 'articlePage.articleReviewerBy' },
                          {
                            name: expert?.name
                              ? `<a data-event-category="Reviewer Name" data-event-action="${expert?.name}" data-event-label="https://${domainLocales[sL]}${expert?.permalink}" href="${expert?.permalink}">${expert?.name}</a>`
                              : `<a data-event-category="Reviewer Name" data-event-action="${reviewer?.displayName}" data-event-label="https://${domainLocales[sL]}${reviewer?.profileUrl}" href="${reviewer?.profileUrl}">${reviewer?.displayName}</a>`,
                          },
                        ),
                      }}
                    />
                  ) : null,
                  <span
                    key={3}
                    className="reviewer-info"
                    dangerouslySetInnerHTML={{
                      __html: f(
                        { id: 'articlePage.articleUpdatedAt' },
                        {
                          date: updatedAt,
                        },
                      ),
                    }}
                  />,
                ]
          }
          separator={<StyledDotSeparator />}
          className="author-content"
          size="sm"
        />
      </IconText>
      <BookingBtn
        size="sm"
        locale={locale}
        bookingId={String(articleReviewed?.expert?.['care-booking']?.id || '')}
        bookingType={
          articleReviewed?.expert?.['care-booking']?.type || undefined
        }
      />
    </StyledAuthorAndDate>
  )
}

export { AuthorAndDate }
